import normalizeFilterValue from './normalizeFilterValue';
import parsePositiveIntegerString from './parsePositiveIntegerString';
import toSearchString from './toSearchString';

const STRING_KEYS = Object.freeze(['term', 'date_from', 'date_to', 'where', 'place_id']);
const POSITIVE_INT_KEYS = Object.freeze(['category', 'page']);
const LEGACY_FORM_KEY_PREFIX = 'event_filter[';
const LEGACY_STRING_ALIASES = Object.freeze({ query: 'term', 'event_filter[event]': 'term' });
const ATTRIBUTION_KEYS = Object.freeze([
    'fbclid', 'gclid', 'gbraid', 'wbraid', 'dclid', 'msclkid', 'ttclid', 'twclid', 'li_fat_id', 'yclid',
    'igshid', 'srsltid', 'mc_cid', 'mc_eid', '_ga', '_gl',
]);
const ATTRIBUTION_KEY_PREFIX = 'utm_';
const DEFAULT_PAGE = 1;

function toSearchParams(searchString) {
    if (typeof searchString !== 'string') {
        return new URLSearchParams();
    }

    return new URLSearchParams(searchString.startsWith('?') ? searchString.slice(1) : searchString);
}

function parsePositiveInt(value) {
    const trimmed = normalizeFilterValue(value);
    if (trimmed === undefined) {
        return undefined;
    }
    const parsed = parsePositiveIntegerString(trimmed);
    return parsed === null ? undefined : parsed;
}

export function parseSearchParams(searchString) {
    const source = toSearchParams(searchString);

    const result = {};

    STRING_KEYS.forEach((key) => {
        const value = normalizeFilterValue(source.get(key));
        if (value !== undefined) {
            result[key] = value;
        }
    });

    Object.entries(LEGACY_STRING_ALIASES).forEach(([legacyKey, canonicalKey]) => {
        if (result[canonicalKey] !== undefined) {
            return;
        }
        const value = normalizeFilterValue(source.get(legacyKey));
        if (value !== undefined) {
            result[canonicalKey] = value;
        }
    });

    POSITIVE_INT_KEYS.forEach((key) => {
        const value = parsePositiveInt(source.get(key));
        if (value !== undefined) {
            result[key] = value;
        }
    });

    return result;
}

function isAttributionKey(key) {
    return key.startsWith(ATTRIBUTION_KEY_PREFIX) || ATTRIBUTION_KEYS.includes(key);
}

function isSearchInputKey(key) {
    return key !== 'page' && !isAttributionKey(key);
}

export function isUnresolvedSearch(searchString) {
    const suppliedKeys = Array.from(toSearchParams(searchString).keys());
    const resolvedKeys = Object.keys(parseSearchParams(searchString));

    return suppliedKeys.some(isSearchInputKey) && !resolvedKeys.some(isSearchInputKey);
}

export function hasLegacySearchValue(searchString) {
    const source = toSearchParams(searchString);

    return Object.keys(LEGACY_STRING_ALIASES)
        .some(legacyKey => normalizeFilterValue(source.get(legacyKey)) !== undefined);
}

function isLegacyFormKey(key) {
    return key.startsWith(LEGACY_FORM_KEY_PREFIX);
}

export function canonicaliseLegacySearch(searchString) {
    const params = toSearchParams(searchString);
    const state = parseSearchParams(searchString);

    Array.from(params.keys()).filter(isLegacyFormKey).forEach(key => params.delete(key));

    Object.entries(LEGACY_STRING_ALIASES).forEach(([legacyKey, canonicalKey]) => {
        params.delete(legacyKey);
        if (state[canonicalKey] !== undefined) {
            params.set(canonicalKey, state[canonicalKey]);
        }
    });

    return toSearchString(params);
}

export function buildSearchParams(state = {}) {
    const params = new URLSearchParams();

    STRING_KEYS.forEach((key) => {
        const value = normalizeFilterValue(state[key]);
        if (value !== undefined) {
            params.append(key, value);
        }
    });

    POSITIVE_INT_KEYS.forEach((key) => {
        const value = state[key];
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 1) {
            return;
        }
        if (key === 'page' && value === DEFAULT_PAGE) {
            return;
        }
        params.append(key, String(Math.floor(value)));
    });

    return params;
}

export function buildSearchString(state) {
    return toSearchString(buildSearchParams(state));
}

export { DEFAULT_PAGE };
